import React, { useEffect, useState, useCallback } from 'react';
import ToastContainer from '../components/ToastContainer';
import ProductModal from '../components/ProductModal';
import HeroSlideModal from '../components/HeroSlideModal';
import ReportsPanel from '../components/ReportsPanel';
import { getSession, isAdmin, signOut, getMyProfile, updateUserEmail, updateUserPassword } from '../services/auth';
import { listProducts, createProduct, updateProduct, deleteProduct, uploadProductImage, getProduct } from '../services/products';
import { listPendingReviews, approveReview, rejectReview, updateAdminReply } from '../services/reviews';
import { listOrders, updateOrderStatus, markOrderAsCompleted } from '../services/orders';
import { getSettings, updateSetting, getAllHeroSlides, createHeroSlide, updateHeroSlide, deleteHeroSlide, uploadImage, getCategories, addCategory, deleteCategory, updateCategory } from '../services/settings';
import { supabase } from '../lib/supabase';

const AdminPanelEnhanced = () => {
  const [authorized, setAuthorized] = useState(false);
  const [activeTab, setActiveTab] = useState('orders');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(true);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedCustomerOrders, setSelectedCustomerOrders] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Payment verification modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPaymentOrder, setSelectedPaymentOrder] = useState(null);

  // Order details modal
  const [showOrderDetailsModal, setShowOrderDetailsModal] = useState(false);
  const [selectedOrderForDetails, setSelectedOrderForDetails] = useState(null);

  // Toast notifications
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, type = 'success', duration = 3000) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type, duration }]);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  // Product modal
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [lastRefresh, setLastRefresh] = useState(null);
  const [orderFilter, setOrderFilter] = useState('all'); // 'all', 'pending', 'confirmed', 'delivered', 'cancelled'

  // Pagination states
  const [productsDisplayed, setProductsDisplayed] = useState(20);
  const [ordersDisplayed, setOrdersDisplayed] = useState(20);
  const [customersDisplayed, setCustomersDisplayed] = useState(20);

  // Search states
  const [orderSearchQuery, setOrderSearchQuery] = useState('');
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [searchFilters, setSearchFilters] = useState({
    customerName: '',
    phone: '',
    orderId: '',
    dateFrom: '',
    dateTo: '',
    minAmount: '',
    maxAmount: ''
  });

  // Enhanced setSuccess with toast
  const enhancedSetSuccess = (message) => {
    setSuccess(message);
    showToast(message, 'success');
  };

  // Enhanced setError with toast
  const enhancedSetError = (message) => {
    setError(message);
    showToast(message, 'error', 5000);
  };

  // Settings and Hero Slides
  const [settings, setSettings] = useState({ company_name: '', company_logo: '', whatsapp_number: '' });
  const [heroSlides, setHeroSlides] = useState([]);
  const [showHeroSlideModal, setShowHeroSlideModal] = useState(false);
  const [editingHeroSlide, setEditingHeroSlide] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoImageError, setLogoImageError] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploadingProductImage, setIsUploadingProductImage] = useState(false);
  const [isUploadingHeroImage, setIsUploadingHeroImage] = useState(false);

  // Category management
  const [categories, setCategories] = useState(['আচার', 'মিষ্টি', 'খিচুড়ি', 'তরকারি']);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [selectedProductCategory, setSelectedProductCategory] = useState('all'); // For filtering products by category
  const [showCategoryManagementOnProducts, setShowCategoryManagementOnProducts] = useState(false); // Toggle category management on products page
  const [activeSettingsSection, setActiveSettingsSection] = useState('all'); // For settings page section navigation: 'all', 'company', 'payment', 'categories', 'hero'

  // Reviews moderation
  const [showReviewsModal, setShowReviewsModal] = useState(false);
  const [selectedProductForReviews, setSelectedProductForReviews] = useState(null);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [adminReplies, setAdminReplies] = useState({});
  const [approvedReviews, setApprovedReviews] = useState([]);
  const [reviewsTab, setReviewsTab] = useState('pending'); // 'pending' | 'approved'
  const [pendingCounts, setPendingCounts] = useState({}); // product_id -> count

  // Profile settings
  const [profileEmail, setProfileEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');


  useEffect(() => {
    (async () => {
      try {
        const session = await getSession();
        if (!session) {
          window.location.href = '/admin/login';
          return;
        }
        const admin = await isAdmin();
        if (!admin) {
          setAuthorized(false);
          setLoading(false);
          return;
        }
        setAuthorized(true);
        const myProfile = await getMyProfile();
        setProfile(myProfile);
        setProfileEmail(myProfile?.email || '');
        await refreshData();
        setLoading(false);
      } catch (err) {
        console.error('AdminPanel useEffect error:', err);
        setError(err.message || 'Failed to initialize admin panel');
        setLoading(false);
      }
    })();
  }, []);

  // Auto-refresh orders every 30 seconds when on orders tab
  useEffect(() => {
    if (!authorized || activeTab !== 'orders') return;

    const interval = setInterval(() => {
      refreshData().catch(err => {
        console.error('Auto-refresh error:', err);
      });
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [authorized, activeTab]);

  // Load settings when settings tab is opened
  useEffect(() => {
    if (activeTab === 'settings') {
      loadSettings();
      loadCategories();
    }
  }, [activeTab]);

  // Load categories on mount
  useEffect(() => {
    loadCategories();
  }, []);

  async function loadCategories() {
    try {
      const cats = await getCategories();
      setCategories(cats);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  }

  async function loadPendingReviewCounts() {
    try {
      const allPending = await listPendingReviews(null);
      const counts = {};
      allPending.forEach(r => {
        const pid = r.product_id;
        counts[pid] = (counts[pid] || 0) + 1;
      });
      setPendingCounts(counts);
    } catch (err) {
      console.error('Failed to load pending review counts:', err);
    }
  }

  async function refreshData() {
    try {
      const [p, o, u] = await Promise.all([listProducts(), listOrders(), listUsers()]);
      setProducts(p);
      setOrders(o);
      setUsers(u);
      setLastRefresh(new Date());
      // Reload categories to ensure they're up to date
      await loadCategories();
      // Load pending review counts
      await loadPendingReviewCounts();
    } catch (err) {
      enhancedSetError(err.message || 'Failed to load data');
    }
  }

  async function openReviewsModal(product) {
    try {
      setSelectedProductForReviews(product);
      const rows = await listPendingReviews(product?.id);
      setPendingReviews(rows || []);
      // load approved via RPC: reuse reviews service through supabase directly here
      const { data: approved, error } = await supabase
        .from('product_reviews')
        .select('*')
        .eq('product_id', product?.id)
        .eq('status', 'approved')
        .order('created_at', { ascending: false });
      if (!error) setApprovedReviews(approved || []);
      setReviewsTab('pending');
      setShowReviewsModal(true);
    } catch (err) {
      enhancedSetError(err.message || 'Failed to load reviews');
    }
  }

  async function loadSettings() {
    try {
      const data = await getSettings();
      if (data) setSettings(data);
      const slides = await getAllHeroSlides();
      if (slides) setHeroSlides(slides);
    } catch (err) {
      enhancedSetError(err.message || 'Failed to load settings');
    }
  }

  const handleHeroSlideSave = async (form, imageFile) => {
    try {
      setIsUploadingHeroImage(true);
      let imageUrl = form.image_url;
      if (imageFile) {
        showToast('Uploading hero slide image... Please wait', 'info', 5000);
        imageUrl = await uploadImage(imageFile, `slide-${Date.now()}.${imageFile.name.split('.').pop()}`);
      }

      const slideData = { ...form, image_url: imageUrl };

      if (editingHeroSlide) {
        await updateHeroSlide(editingHeroSlide.id, slideData);
        enhancedSetSuccess('Hero slide updated!');
      } else {
        await createHeroSlide(slideData);
        enhancedSetSuccess('Hero slide created!');
      }

      setIsUploadingHeroImage(false);
      setShowHeroSlideModal(false);
      setEditingHeroSlide(null);
      await loadSettings();
    } catch (err) {
      setIsUploadingHeroImage(false);
      enhancedSetError(err.message || 'Failed to save hero slide');
    }
  };

  const handleEditHeroSlide = (slide) => {
    setEditingHeroSlide(slide);
    setShowHeroSlideModal(true);
  };

  const handleAddHeroSlide = () => {
    setEditingHeroSlide(null);
    setShowHeroSlideModal(true);
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      enhancedSetError('Category name cannot be empty');
      return;
    }
    if (categories.includes(newCategoryName.trim())) {
      enhancedSetError('Category already exists');
      return;
    }
    try {
      const updated = await addCategory(newCategoryName.trim());
      setCategories(updated);
      setNewCategoryName('');
      // Reload categories from database to ensure sync
      await loadCategories();
      enhancedSetSuccess('Category added successfully!');
    } catch (err) {
      console.error('Error adding category:', err);
      enhancedSetError(err.message || 'Failed to add category');
      // Reload categories even on error to sync with database
      await loadCategories();
    }
  };

  const handleDeleteCategory = async (categoryName) => {
    if (!window.confirm(`Delete category "${categoryName}"? Products with this category will keep the category name.`)) return;
    try {
      const updated = await deleteCategory(categoryName);
      setCategories(updated);
      // If the deleted category was selected in the product filter, reset to "all"
      if (selectedProductCategory === categoryName) {
        setSelectedProductCategory('all');
      }
      // Reload categories from database to ensure sync
      await loadCategories();
      enhancedSetSuccess('Category deleted successfully!');
    } catch (err) {
      console.error('Error deleting category:', err);
      enhancedSetError(err.message || 'Failed to delete category');
      // Reload categories even on error to sync with database
      await loadCategories();
    }
  };

  const handleEditCategory = (oldName) => {
    setEditingCategory(oldName);
    setNewCategoryName(oldName);
  };

  const handleUpdateCategory = async () => {
    if (!newCategoryName.trim()) {
      enhancedSetError('Category name cannot be empty');
      return;
    }
    if (newCategoryName.trim() === editingCategory) {
      setEditingCategory(null);
      setNewCategoryName('');
      return;
    }
    if (categories.includes(newCategoryName.trim()) && newCategoryName.trim() !== editingCategory) {
      enhancedSetError('Category already exists');
      return;
    }
    try {
      const oldCategoryName = editingCategory;
      const updated = await updateCategory(editingCategory, newCategoryName.trim());
      setCategories(updated);
      // If the updated category was selected in the product filter, update the filter
      if (selectedProductCategory === oldCategoryName) {
        setSelectedProductCategory(newCategoryName.trim());
      }
      setEditingCategory(null);
      setNewCategoryName('');
      // Reload categories from database to ensure sync
      await loadCategories();
      enhancedSetSuccess('Category updated successfully!');
    } catch (err) {
      console.error('Error updating category:', err);
      enhancedSetError(err.message || 'Failed to update category');
      // Reload categories even on error to sync with database
      await loadCategories();
    }
  };

  async function listUsers() {
    const { data, error } = await supabase
      .from('customers')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  }

  const handleEditProduct = async (productId) => {
    try {
      const product = await getProduct(productId);
      setEditingProduct(product);
      setShowProductModal(true);
    } catch (err) {
      enhancedSetError(err.message || 'Failed to load product');
    }
  };

  const handleAddProduct = () => {
    setEditingProduct(null);
    setShowProductModal(true);
  };

  const handleProductSave = async (form, imageFile) => {
    setError('');
    setSuccess('');
    try {
      setIsUploadingProductImage(true);
      let image_url = form.image_url;
      if (imageFile) {
        showToast('Uploading product image... Please wait', 'info', 5000);
        image_url = await uploadProductImage(imageFile, imageFile.name);
      }

      if (form.id) {
        // Update existing product
        await updateProduct(form.id, {
          name_bn: form.name_bn,
          name_en: form.name_en || null,
          description_bn: form.description_bn || null,
          price: parseInt(form.price || '0', 10),
          offer_price: form.offer_price ? parseInt(form.offer_price, 10) : null,
          category: form.category || null,
          image_url
        });
        enhancedSetSuccess('Product updated successfully!');
      } else {
        // Create new product
        await createProduct({
          name_bn: form.name_bn,
          name_en: form.name_en || null,
          description_bn: form.description_bn || null,
          price: parseInt(form.price || '0', 10),
          offer_price: form.offer_price ? parseInt(form.offer_price, 10) : null,
          category: form.category || null,
          image_url,
          is_active: true
        });
        enhancedSetSuccess('Product created successfully!');
      }

      setIsUploadingProductImage(false);
      setShowProductModal(false);
      setEditingProduct(null);
      await refreshData();
    } catch (err) {
      setIsUploadingProductImage(false);
      enhancedSetError(err.message || 'Failed to save product');
    }
  }

  async function handleDeleteProduct(id) {
    if (!window.confirm('Delete this product?')) return;
    try {
      await deleteProduct(id);
      setSuccess('Product deleted successfully!');
      showToast('Product deleted successfully!', 'success');
      await refreshData();
    } catch (err) {
      setError(err.message || 'Failed to delete product');
    }
  }

  async function handleOrderStatus(id, status) {
    try {
      let paymentStatus = null;
      if (status === 'confirmed' || status === 'delivered') {
        paymentStatus = 'paid';
      } else if (status === 'cancelled' || status === 'returned') {
        paymentStatus = 'unpaid';
      }

      await updateOrderStatus(id, status, paymentStatus);
      setSuccess('Order updated successfully!');
      showToast('Order status updated successfully!', 'success');
      setTimeout(() => setSuccess(''), 3000);

      // Refresh all data
      await refreshData();

      // Update the modal state if it's open
      if (showOrderModal && selectedCustomerOrders.length > 0 && selectedCustomer) {
        const updatedOrders = await listOrders();
        const customerOrders = updatedOrders.filter(o => o.customers?.id === selectedCustomer.id);
        setSelectedCustomerOrders(customerOrders);
      }
    } catch (err) {
      setError(err.message || 'Failed to update order');
      setTimeout(() => setError(''), 5000);
    }
  }

  async function handleMarkAsCompleted(orderId) {
    try {
      await markOrderAsCompleted(orderId);
      setSuccess('Order marked as completed!');
      showToast('Order marked as completed!', 'success');
      setTimeout(() => setSuccess(''), 3000);

      // Refresh all data
      await refreshData();

      // Update the modal state if it's open
      if (showOrderModal && selectedCustomerOrders.length > 0 && selectedCustomer) {
        const updatedOrders = await listOrders();
        const customerOrders = updatedOrders.filter(o => o.customers?.id === selectedCustomer.id);
        setSelectedCustomerOrders(customerOrders);
      }
    } catch (err) {
      setError(err.message || 'Failed to mark order as completed');
      setTimeout(() => setError(''), 5000);
    }
  }

  // Helper function to check if order is within return period
  // Returns true only if: delivered, within return period, and NOT completed
  function isWithinReturnPeriod(order, returnDays = 7) {
    // Must be delivered
    if (order.status !== 'delivered') return false;

    // Must have delivery date
    if (!order.delivered_at) return false;

    // Can't return if already marked as satisfied/completed
    if (order.status === 'completed' || order.completed_at) return false;

    // Check if within return period (7 days from delivery)
    const deliveredDate = new Date(order.delivered_at);
    const returnDeadline = new Date(deliveredDate);
    returnDeadline.setDate(returnDeadline.getDate() + returnDays);
    const now = new Date();

    return now <= returnDeadline;
  }

  async function handlePaymentVerification(orderId, accepted) {
    try {
      if (accepted) {
        // Accept payment: Mark as paid and confirm
        await updateOrderStatus(orderId, 'confirmed', 'paid');
        setSuccess('Payment accepted! Order confirmed.');
        showToast('Payment accepted! Order confirmed successfully.', 'success');
      } else {
        // Reject payment: Cancel order with payment_failed status
        await updateOrderStatus(orderId, 'payment_failed', 'payment_failed');
        setSuccess('Payment rejected! Order cancelled.');
        showToast('Payment rejected! Order has been cancelled.', 'error');
      }
      setTimeout(() => setSuccess(''), 3000);

      // Refresh all data
      await refreshData();

      // Update the modal state if it's open
      if (showOrderModal && selectedCustomerOrders.length > 0 && selectedCustomer) {
        const updatedOrders = await listOrders();
        const customerOrders = updatedOrders.filter(o => o.customers?.id === selectedCustomer.id);
        setSelectedCustomerOrders(customerOrders);
      }
    } catch (err) {
      setError(err.message || 'Failed to verify payment');
      setTimeout(() => setError(''), 5000);
    }
  }

  // Filter and search orders
  const filteredOrders = orders.filter(order => {
    // Status filter
    if (orderFilter !== 'all' && order.status !== orderFilter) return false;

    // Simple search query
    if (orderSearchQuery) {
      const query = orderSearchQuery.toLowerCase();
      const matchesId = order.id.toLowerCase().includes(query);
      const matchesCustomerName = (order.customers?.name || '').toLowerCase().includes(query);
      const matchesPhone = (order.customers?.phone || '').includes(query);
      const matchesStatus = order.status.toLowerCase().includes(query);

      if (!matchesId && !matchesCustomerName && !matchesPhone && !matchesStatus) return false;
    }

    // Advanced search filters
    if (showAdvancedSearch) {
      if (searchFilters.customerName && !(order.customers?.name || '').toLowerCase().includes(searchFilters.customerName.toLowerCase())) return false;
      if (searchFilters.phone && !(order.customers?.phone || '').includes(searchFilters.phone)) return false;
      if (searchFilters.orderId && !order.id.toLowerCase().includes(searchFilters.orderId.toLowerCase())) return false;

      if (searchFilters.dateFrom) {
        const orderDate = new Date(order.created_at);
        const fromDate = new Date(searchFilters.dateFrom);
        if (orderDate < fromDate) return false;
      }

      if (searchFilters.dateTo) {
        const orderDate = new Date(order.created_at);
        const toDate = new Date(searchFilters.dateTo);
        toDate.setHours(23, 59, 59, 999);
        if (orderDate > toDate) return false;
      }

      if (searchFilters.minAmount || searchFilters.maxAmount) {
        const orderTotal = (order.order_items || []).reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
        if (searchFilters.minAmount && orderTotal < parseFloat(searchFilters.minAmount)) return false;
        if (searchFilters.maxAmount && orderTotal > parseFloat(searchFilters.maxAmount)) return false;
      }
    }

    return true;
  });

  // Filter and search customers
  const filteredCustomers = users.filter(customer => {
    if (customerSearchQuery) {
      const query = customerSearchQuery.toLowerCase();
      const matchesName = (customer.name || '').toLowerCase().includes(query);
      const matchesPhone = (customer.phone || '').includes(query);
      const matchesAddress = (customer.address || '').toLowerCase().includes(query);
      const matchesDistrict = (customer.district || '').toLowerCase().includes(query);

      if (!matchesName && !matchesPhone && !matchesAddress && !matchesDistrict) return false;
    }
    return true;
  });

  // Filter products by category
  const filteredProducts = products.filter(product => {
    if (selectedProductCategory === 'all') return true;
    return product.category === selectedProductCategory;
  });

  // Paginated data
  const displayedProducts = filteredProducts.slice(0, productsDisplayed);
  const displayedOrders = filteredOrders.slice(0, ordersDisplayed);
  const displayedCustomers = filteredCustomers.slice(0, customersDisplayed);
  const hasMoreProducts = filteredProducts.length > productsDisplayed;
  const hasMoreOrders = filteredOrders.length > ordersDisplayed;
  const hasMoreCustomers = filteredCustomers.length > customersDisplayed;

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
      <div className="text-xl font-semibold text-slate-700">Loading...</div>
    </div>
  );

  if (!authorized) return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
      <div className="text-xl text-red-600 font-semibold">Not authorized. Redirecting to login...</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-gray-50 to-slate-100">
      {/* Toast Container */}
      {toasts.length > 0 && (
        <ToastContainer toasts={toasts} removeToast={removeToast} />
      )}

      {/* Header */}
      <header className="bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800 shadow-xl border-b border-slate-600 sticky top-0 z-40" style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center h-auto sm:h-16 py-3 sm:py-0 gap-3 sm:gap-0">
            <h1 className="text-xl sm:text-2xl font-bold text-white bg-gradient-to-r from-white to-slate-200 bg-clip-text text-transparent" style={{ WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Admin Dashboard
            </h1>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 w-full sm:w-auto">
              {profile && (
                <div className="text-xs sm:text-sm text-slate-300 truncate max-w-[200px] sm:max-w-none">
                  {profile.email}
                </div>
              )}
              <button
                onClick={async () => { await signOut(); window.location.href = '/'; }}
                className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-lg transition-all transform hover:scale-105 active:scale-95 font-semibold shadow-lg w-full sm:w-auto"
                style={{ boxShadow: '0 4px 15px rgba(220, 38, 38, 0.4)' }}
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Onboarding Banner */}
      {showOnboarding && (
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 border-b border-orange-400 shadow-md">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-2 flex-1">
                <svg className="w-5 h-5 text-white flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                </svg>
                <span className="text-sm text-white">
                  Welcome! Manage your products, orders, and customers here.
                </span>
              </div>
              <button
                onClick={() => setShowOnboarding(false)}
                className="text-white hover:text-orange-100 text-sm font-semibold px-3 py-1 rounded-lg hover:bg-white/20 transition-colors"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reviews Moderation Modal */}
      {showReviewsModal && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-2 sm:p-4"
          onClick={() => {
            setShowReviewsModal(false);
            setSelectedProductForReviews(null);
            setPendingReviews([]);
            loadPendingReviewCounts(); // Refresh counts after closing
          }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-2xl w-full max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 sm:p-6 border-b flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-slate-900">Pending Reviews</h3>
                {selectedProductForReviews && (
                  <p className="text-sm text-slate-600">Product: {selectedProductForReviews.name_bn}</p>
                )}
              </div>
              <button
                onClick={() => {
                  setShowReviewsModal(false);
                  setSelectedProductForReviews(null);
                  setPendingReviews([]);
                  loadPendingReviewCounts(); // Refresh counts after closing
                }}
                className="text-slate-500 hover:text-slate-700 p-2 rounded-lg hover:bg-slate-100"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 sm:p-6 overflow-y-auto">
              {/* Tabs */}
              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => setReviewsTab('pending')}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold ${reviewsTab === 'pending' ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                >
                  Pending
                </button>
                <button
                  onClick={() => setReviewsTab('approved')}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold ${reviewsTab === 'approved' ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                >
                  Approved
                </button>
              </div>

              {reviewsTab === 'pending' ? (
                pendingReviews.length === 0 ? (
                  <div className="text-slate-500 text-center py-8">No pending reviews</div>
                ) : (
                  <div className="space-y-3">
                    {pendingReviews.map((r) => (
                      <div key={r.id} className="border rounded-xl p-3 bg-slate-50">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-slate-800">{r.customer_name}</span>
                          <span className="text-orange-600 font-semibold">★ {r.rating}/5</span>
                        </div>
                        <p className="text-slate-700 mt-1 whitespace-pre-line">{r.comment}</p>
                        <div className="mt-2">
                          <input
                            type="text"
                            placeholder="Admin reply (optional)"
                            className="w-full px-3 py-2 border rounded-lg"
                            value={adminReplies[r.id] || ''}
                            onChange={(e) => setAdminReplies((prev) => ({ ...prev, [r.id]: e.target.value }))}
                          />
                        </div>
                        <div className="flex gap-2 mt-3">
                          <button
                            onClick={async () => {
                              try {
                                await approveReview(r.id, adminReplies[r.id] || null);
                                setPendingReviews((prev) => prev.filter((x) => x.id !== r.id));
                                setPendingCounts((prev) => {
                                  const pid = r.product_id;
                                  const newCount = Math.max(0, (prev[pid] || 0) - 1);
                                  if (newCount === 0) {
                                    const { [pid]: _, ...rest } = prev;
                                    return rest;
                                  }
                                  return { ...prev, [pid]: newCount };
                                });
                                enhancedSetSuccess('Review approved');
                              } catch (e) { enhancedSetError(e.message || 'Failed to approve'); }
                            }}
                            className="px-3 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-semibold"
                          >
                            Approve
                          </button>
                          <button
                            onClick={async () => {
                              try {
                                await rejectReview(r.id);
                                setPendingReviews((prev) => prev.filter((x) => x.id !== r.id));
                                setPendingCounts((prev) => {
                                  const pid = r.product_id;
                                  const newCount = Math.max(0, (prev[pid] || 0) - 1);
                                  if (newCount === 0) {
                                    const { [pid]: _, ...rest } = prev;
                                    return rest;
                                  }
                                  return { ...prev, [pid]: newCount };
                                });
                                enhancedSetSuccess('Review rejected');
                              } catch (e) { enhancedSetError(e.message || 'Failed to reject'); }
                            }}
                            className="px-3 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold"
                          >
                            Reject
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              ) : (
                approvedReviews.length === 0 ? (
                  <div className="text-slate-500 text-center py-8">No approved reviews</div>
                ) : (
                  <div className="space-y-3">
                    {approvedReviews.map((r) => (
                      <div key={r.id} className="border rounded-xl p-3 bg-white">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-slate-800">{r.customer_name}</span>
                          <span className="text-orange-600 font-semibold">★ {r.rating}/5</span>
                        </div>
                        <p className="text-slate-700 mt-1 whitespace-pre-line">{r.comment}</p>
                        <div className="mt-2">
                          <input
                            type="text"
                            placeholder="Edit admin reply"
                            className="w-full px-3 py-2 border rounded-lg"
                            value={adminReplies[r.id] ?? r.admin_reply ?? ''}
                            onChange={(e) => setAdminReplies((prev) => ({ ...prev, [r.id]: e.target.value }))}
                          />
                        </div>
                        <div className="flex gap-2 mt-3">
                          <button
                            onClick={async () => {
                              try {
                                const updated = await updateAdminReply(r.id, adminReplies[r.id] ?? r.admin_reply ?? '');
                                setApprovedReviews((prev) => prev.map((x) => x.id === r.id ? updated : x));
                                enhancedSetSuccess('Reply updated');
                              } catch (e) { enhancedSetError(e.message || 'Failed to update reply'); }
                            }}
                            className="px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold"
                          >
                            Save Reply
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>
            <div className="border-t p-3 bg-white/70">
              <button
                onClick={() => {
                  setShowReviewsModal(false);
                  setSelectedProductForReviews(null);
                  setPendingReviews([]);
                  loadPendingReviewCounts(); // Refresh counts after closing
                }}
                className="w-full px-4 py-3 border-2 border-slate-300 text-slate-700 hover:bg-slate-100 rounded-lg font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Messages */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
            {success}
          </div>
        )}
      </div>

      {/* Iconic Tabs - Desktop */}
      <div className="hidden md:block border-b bg-gradient-to-r from-white to-slate-50 shadow-sm sticky top-[64px] sm:top-[73px] z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center space-x-2 lg:space-x-4">
            {[
              {
                id: 'products',
                label: 'Products',
                count: products.length,
                icon: (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                )
              },
              {
                id: 'orders',
                label: 'Orders',
                count: orders.length,
                icon: (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                )
              },
              {
                id: 'customers',
                label: 'Customers',
                count: users.length,
                icon: (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.196-2.134M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.196-2.134M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                )
              },
              {
                id: 'reports',
                label: 'Reports',
                count: null,
                icon: (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                )
              },
              {
                id: 'settings',
                label: 'Settings',
                count: null,
                icon: (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                )
              },
              {
                id: 'profile',
                label: 'Profile',
                count: null,
                icon: (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                )
              }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-3 sm:py-4 px-3 sm:px-6 border-b-2 font-semibold text-xs sm:text-sm transition-all rounded-t-lg ${activeTab === tab.id
                  ? 'border-slate-700 text-slate-800 bg-gradient-to-b from-slate-50 to-white shadow-sm'
                  : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300 hover:bg-slate-50'
                  }`}
                style={activeTab === tab.id ? {
                  boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.8)'
                } : {}}
              >
                <span className={`${activeTab === tab.id ? 'text-slate-700' : 'text-gray-500'}`}>
                  {tab.icon}
                </span>
                <span>{tab.label}</span>
                {tab.count !== null && (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${activeTab === tab.id
                    ? 'bg-orange-600 text-white'
                    : 'bg-orange-100 text-orange-600'
                    }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Products Tab */}
        {activeTab === 'products' && (
          <div className="space-y-6">
            {/* Header with Add Button */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h2 className="text-2xl font-bold text-slate-800">Products ({filteredProducts.length})</h2>
                {selectedProductCategory !== 'all' && (
                  <div className="text-sm text-slate-500 mt-1">
                    Filtered by: {selectedProductCategory} (Total: {products.length})
                  </div>
                )}
              </div>
              <button
                onClick={handleAddProduct}
                className="px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-slate-700 to-slate-900 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:scale-105 active:scale-95"
                style={{ boxShadow: '0 4px 15px rgba(30, 41, 59, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)' }}
              >
                <span className="hidden sm:inline">+ Add Product</span>
                <span className="sm:hidden">+ Add</span>
              </button>
            </div>

            {/* Category Filter Sub-Menu */}
            <div className="bg-gradient-to-br from-white to-slate-50 rounded-xl shadow-md border border-slate-200 p-4"
              style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.6)' }}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                  <span className="text-sm font-semibold text-slate-700">Filter by Category:</span>
                </div>
                <button
                  onClick={() => setShowCategoryManagementOnProducts(!showCategoryManagementOnProducts)}
                  className="px-3 py-1.5 bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white text-xs font-semibold rounded-lg transition-all transform hover:scale-105 active:scale-95 shadow-md flex items-center gap-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  {showCategoryManagementOnProducts ? 'Hide' : 'Manage'} Categories
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedProductCategory('all')}
                  className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all transform hover:scale-105 active:scale-95 ${selectedProductCategory === 'all'
                    ? 'bg-gradient-to-r from-slate-700 to-slate-900 text-white shadow-lg'
                    : 'bg-white text-slate-700 shadow-md hover:shadow-lg border border-slate-200'
                    }`}
                  style={selectedProductCategory === 'all' ? { boxShadow: '0 4px 15px rgba(30, 41, 59, 0.3)' } : {}}
                >
                  All ({products.length})
                </button>
                {categories.map((category) => {
                  const categoryCount = products.filter(p => p.category === category).length;
                  return (
                    <button
                      key={category}
                      onClick={() => setSelectedProductCategory(category)}
                      className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all transform hover:scale-105 active:scale-95 ${selectedProductCategory === category
                        ? 'bg-gradient-to-r from-slate-700 to-slate-900 text-white shadow-lg'
                        : 'bg-white text-slate-700 shadow-md hover:shadow-lg border border-slate-200'
                        }`}
                      style={selectedProductCategory === category ? { boxShadow: '0 4px 15px rgba(30, 41, 59, 0.3)' } : {}}
                    >
                      {category} ({categoryCount})
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Category Management Section (Collapsible) */}
            {showCategoryManagementOnProducts && (
              <div className="bg-gradient-to-br from-white via-slate-50 to-slate-100 rounded-xl shadow-xl border border-slate-200 p-4 sm:p-6 animate-fade-in"
                style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.6)' }}>
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-300">
                  <div className="flex items-center gap-2">
                    <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                    <h2 className="text-2xl font-bold bg-gradient-to-r from-slate-700 to-slate-900 bg-clip-text text-transparent">Product Categories Management</h2>
                  </div>
                  <button
                    onClick={() => setShowCategoryManagementOnProducts(false)}
                    className="text-slate-500 hover:text-slate-700 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="space-y-4">
                  {/* Add Category Form */}
                  <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-inner">
                    {editingCategory ? (
                      <div className="flex flex-col sm:flex-row gap-3">
                        <input
                          type="text"
                          value={newCategoryName}
                          onChange={(e) => setNewCategoryName(e.target.value)}
                          className="flex-1 px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-all shadow-inner"
                          placeholder="Edit category name"
                          onKeyPress={(e) => e.key === 'Enter' && handleUpdateCategory()}
                        />
                        <button
                          onClick={handleUpdateCategory}
                          className="px-4 py-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold rounded-xl transition-all transform hover:scale-105 active:scale-95 shadow-md"
                        >
                          Update
                        </button>
                        <button
                          onClick={() => {
                            setEditingCategory(null);
                            setNewCategoryName('');
                          }}
                          className="px-4 py-3 bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white font-semibold rounded-xl transition-all transform hover:scale-105 active:scale-95 shadow-md"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-col sm:flex-row gap-3">
                        <input
                          type="text"
                          value={newCategoryName}
                          onChange={(e) => setNewCategoryName(e.target.value)}
                          className="flex-1 px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-all shadow-inner"
                          placeholder="Enter new category name"
                          onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
                        />
                        <button
                          onClick={handleAddCategory}
                          className="px-4 py-3 bg-gradient-to-r from-slate-700 to-slate-900 hover:from-slate-800 hover:to-slate-900 text-white font-semibold rounded-xl transition-all transform hover:scale-105 active:scale-95 shadow-md"
                          style={{ boxShadow: '0 4px 15px rgba(30, 41, 59, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)' }}
                        >
                          Add Category
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Categories List */}
                  <div>
                    <h3 className="text-lg font-semibold text-slate-700 mb-3">Current Categories ({categories.length})</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {categories.map((category) => {
                        const categoryProductCount = products.filter(p => p.category === category).length;
                        return (
                          <div key={category} className="bg-white border border-slate-200 rounded-xl p-4 shadow-md flex items-center justify-between hover:shadow-lg transition-shadow">
                            <div className="flex-1">
                              <span className="font-semibold text-slate-800 block">{category}</span>
                              <span className="text-xs text-slate-500 mt-1">
                                {categoryProductCount} {categoryProductCount === 1 ? 'product' : 'products'}
                              </span>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEditCategory(category)}
                                className="px-3 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg transition-all transform hover:scale-105 active:scale-95 text-sm shadow-md"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteCategory(category)}
                                className="px-3 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold rounded-lg transition-all transform hover:scale-105 active:scale-95 text-sm shadow-md"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {categories.length === 0 && (
                      <div className="text-center py-8 text-slate-500 bg-white border border-slate-200 rounded-xl">
                        <p>No categories found. Add a new category to get started.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Products Grid - Mobile Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {displayedProducts.map((p) => (
                <div
                  key={p.id}
                  className="bg-gradient-to-br from-white to-slate-50 rounded-xl shadow-md hover:shadow-xl transition-all border border-slate-200 p-4 overflow-hidden"
                  style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.6)' }}
                >
                  {p.image_url && (
                    <img
                      src={p.image_url}
                      alt={p.name_bn}
                      className="w-full h-40 object-cover rounded-lg mb-3 border border-slate-200"
                    />
                  )}
                  <div className="space-y-2">
                    <div className="font-bold text-slate-800 text-lg">{p.name_bn}</div>
                    {p.name_en && <div className="text-sm text-slate-600">{p.name_en}</div>}
                    <div className="flex items-center gap-3 flex-wrap">
                      {p.offer_price ? (
                        <>
                          <span className="text-red-600 font-bold text-lg">৳{p.offer_price}</span>
                          <span className="text-slate-400 line-through text-sm">৳{p.price}</span>
                        </>
                      ) : (
                        <span className="text-slate-700 font-semibold">৳{p.price}</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded-full inline-block">
                      {p.category || '—'}
                    </div>
                    <div className="flex gap-2 pt-2">
                      <button
                        onClick={() => handleEditProduct(p.id)}
                        className="flex-1 px-3 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg transition-all transform hover:scale-105 active:scale-95 text-sm shadow-md"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => openReviewsModal(p)}
                        className="flex-1 px-3 py-2 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-semibold rounded-lg transition-all transform hover:scale-105 active:scale-95 text-sm shadow-md relative"
                      >
                        Reviews
                        {pendingCounts[p.id] > 0 && (
                          <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[18px] text-center font-bold">
                            {pendingCounts[p.id]}
                          </span>
                        )}
                      </button>
                      <button
                        onClick={() => handleDeleteProduct(p.id)}
                        className="flex-1 px-3 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold rounded-lg transition-all transform hover:scale-105 active:scale-95 text-sm shadow-md"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {filteredProducts.length === 0 && (
                <div className="col-span-full text-center py-12 text-slate-500">
                  <p className="text-lg">
                    {selectedProductCategory !== 'all'
                      ? `No products found in category "${selectedProductCategory}".`
                      : 'No products found. Click "Add Product" to create your first product.'}
                  </p>
                </div>
              )}
            </div>

            {/* Load More Button */}
            {hasMoreProducts && (
              <div className="mt-6 text-center">
                <button
                  onClick={() => setProductsDisplayed(prev => prev + 20)}
                  className="px-6 py-3 bg-gradient-to-r from-slate-700 to-slate-900 hover:from-slate-800 hover:to-black text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:scale-105 active:scale-95"
                >
                  Load More ({filteredProducts.length - productsDisplayed} remaining)
                </button>
              </div>
            )}
          </div>
        )}

        {/* Product Modal */}
        <ProductModal
          product={editingProduct}
          isOpen={showProductModal}
          onClose={() => {
            setShowProductModal(false);
            setEditingProduct(null);
          }}
          onSave={handleProductSave}
          categories={categories}
          isUploading={isUploadingProductImage}
        />

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="space-y-6">
            {/* Header with Filter */}
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  <h2 className="text-2xl font-bold text-slate-800">Orders ({filteredOrders.length})</h2>
                  {lastRefresh && (
                    <div className="text-xs text-slate-500 mt-1">
                      Last refreshed: {lastRefresh.toLocaleTimeString()}
                    </div>
                  )}
                </div>
                <div className="flex gap-2 overflow-x-auto pb-2 w-full sm:w-auto">
                  {['all', 'pending', 'confirmed', 'delivered', 'completed', 'cancelled'].map((filter) => (
                    <button
                      key={filter}
                      onClick={() => {
                        setOrderFilter(filter);
                        setOrdersDisplayed(20); // Reset pagination
                      }}
                      className={`px-4 py-2 rounded-lg font-semibold text-sm whitespace-nowrap transition-all transform hover:scale-105 active:scale-95 ${orderFilter === filter
                        ? 'bg-gradient-to-r from-slate-700 to-slate-900 text-white shadow-lg'
                        : 'bg-white text-slate-700 shadow-md hover:shadow-lg border border-slate-200'
                        }`}
                      style={orderFilter === filter ? { boxShadow: '0 4px 15px rgba(30, 41, 59, 0.3)' } : {}}
                    >
                      {filter.charAt(0).toUpperCase() + filter.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Search Bar */}
              <div className="bg-gradient-to-br from-white to-slate-50 rounded-xl shadow-md border border-slate-200 p-4"
                style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.6)' }}>
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      placeholder="Search by Order ID, Customer Name, Phone, or Status..."
                      value={orderSearchQuery}
                      onChange={(e) => {
                        setOrderSearchQuery(e.target.value);
                        setOrdersDisplayed(20); // Reset pagination
                      }}
                      className="w-full px-4 py-3 pl-10 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-all shadow-inner"
                    />
                    <svg className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <button
                    onClick={() => {
                      setShowAdvancedSearch(!showAdvancedSearch);
                      if (!showAdvancedSearch) {
                        setSearchFilters({
                          customerName: '',
                          phone: '',
                          orderId: '',
                          dateFrom: '',
                          dateTo: '',
                          minAmount: '',
                          maxAmount: ''
                        });
                      }
                    }}
                    className="px-4 py-3 bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white font-semibold rounded-xl transition-all transform hover:scale-105 active:scale-95 shadow-md whitespace-nowrap"
                  >
                    {showAdvancedSearch ? 'Hide' : 'Advanced'} Search
                  </button>
                </div>

                {/* Advanced Search Panel */}
                {showAdvancedSearch && (
                  <div className="mt-4 pt-4 border-t border-slate-200 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">Customer Name</label>
                      <input
                        type="text"
                        value={searchFilters.customerName}
                        onChange={(e) => setSearchFilters({ ...searchFilters, customerName: e.target.value })}
                        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-400"
                        placeholder="Customer name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">Phone Number</label>
                      <input
                        type="text"
                        value={searchFilters.phone}
                        onChange={(e) => setSearchFilters({ ...searchFilters, phone: e.target.value })}
                        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-400"
                        placeholder="Phone number"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">Order ID</label>
                      <input
                        type="text"
                        value={searchFilters.orderId}
                        onChange={(e) => setSearchFilters({ ...searchFilters, orderId: e.target.value })}
                        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-400"
                        placeholder="Order ID"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">Date From</label>
                      <input
                        type="date"
                        value={searchFilters.dateFrom}
                        onChange={(e) => setSearchFilters({ ...searchFilters, dateFrom: e.target.value })}
                        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-400"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">Date To</label>
                      <input
                        type="date"
                        value={searchFilters.dateTo}
                        onChange={(e) => setSearchFilters({ ...searchFilters, dateTo: e.target.value })}
                        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-400"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">Min Amount (৳)</label>
                      <input
                        type="number"
                        value={searchFilters.minAmount}
                        onChange={(e) => setSearchFilters({ ...searchFilters, minAmount: e.target.value })}
                        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-400"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">Max Amount (৳)</label>
                      <input
                        type="number"
                        value={searchFilters.maxAmount}
                        onChange={(e) => setSearchFilters({ ...searchFilters, maxAmount: e.target.value })}
                        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-400"
                        placeholder="10000"
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        onClick={() => {
                          setSearchFilters({
                            customerName: '',
                            phone: '',
                            orderId: '',
                            dateFrom: '',
                            dateTo: '',
                            minAmount: '',
                            maxAmount: ''
                          });
                          setOrderSearchQuery('');
                          setOrdersDisplayed(20);
                        }}
                        className="w-full px-4 py-2 bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white font-semibold rounded-lg transition-all transform hover:scale-105 active:scale-95 text-sm shadow-md"
                      >
                        Clear Filters
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Orders Cards - Mobile First */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {displayedOrders.map((o) => {
                // Calculate product subtotal (from order items - stored as integers in taka, e.g., 249 for 249 taka)
                const productSubtotal = (o.order_items || []).reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);

                // Get raw values from database
                const deliveryChargeRaw = o.delivery_charge || 0;
                const transactionFeeRaw = o.transaction_fee || 0;
                const totalAmountRaw = o.total_amount || 0;

                // Format detection: 
                // - Values stored * 100 if > 1000 (new format: 5000 = 50.00)
                // - Values stored as-is if < 1000 (old format: 50 = 50.00)
                // Since delivery charges are typically 50-200, if value < 1000, it's likely direct value
                // Since transaction fees are typically 0-50, if value < 100, it's likely direct value
                const needsDivisionDelivery = deliveryChargeRaw >= 1000;
                const needsDivisionTransaction = transactionFeeRaw >= 100;
                const needsDivisionTotal = totalAmountRaw >= 10000;

                // Apply conversion only if needed
                const deliveryCharge = needsDivisionDelivery ? (deliveryChargeRaw / 100) : deliveryChargeRaw;
                const transactionFee = needsDivisionTransaction ? (transactionFeeRaw / 100) : transactionFeeRaw;

                // Total amount: use stored value if available, otherwise calculate
                const orderTotal = totalAmountRaw > 0
                  ? (needsDivisionTotal ? (totalAmountRaw / 100) : totalAmountRaw)
                  : (productSubtotal + deliveryCharge + transactionFee);
                return (
                  <div
                    key={o.id}
                    className="bg-gradient-to-br from-white to-slate-50 rounded-xl shadow-lg hover:shadow-xl transition-all border border-slate-200 p-4 sm:p-6"
                    style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.6)' }}
                  >
                    <div className="flex justify-between items-start mb-4 pb-4 border-b border-slate-200">
                      <div>
                        <div className="font-bold text-lg text-slate-800">Order #{o.id.substring(0, 8)}</div>
                        <div className="text-sm text-slate-500 mt-1">{new Date(o.created_at).toLocaleString()}</div>
                      </div>
                      <div className="flex flex-col gap-2 items-end">
                        <span className={`px-3 py-1 rounded-lg text-xs font-bold ${o.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          o.status === 'confirmed' ? 'bg-blue-100 text-blue-800' :
                            o.status === 'delivered' ? 'bg-green-100 text-green-800' :
                              o.status === 'completed' ? 'bg-emerald-100 text-emerald-800' :
                                o.status === 'returned' ? 'bg-red-100 text-red-800' :
                                  o.status === 'payment_failed' ? 'bg-purple-100 text-purple-800' :
                                    'bg-gray-100 text-gray-800'
                          }`}>
                          {o.status}
                        </span>
                        <span className={`px-3 py-1 rounded-lg text-xs font-bold ${o.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                          'bg-red-100 text-red-800'
                          }`}>
                          {o.payment_status}
                        </span>
                      </div>
                    </div>

                    <div className="space-y-3 mb-4">
                      <div>
                        <div className="text-sm font-semibold text-slate-700 mb-1">Customer</div>
                        <div className="text-slate-800 font-medium">{o.customers?.name || '—'}</div>
                        <div className="text-sm text-slate-600">{o.customers?.phone}</div>
                      </div>

                      <div>
                        <div className="text-sm font-semibold text-slate-700 mb-1">Items</div>
                        <div className="space-y-1">
                          {(o.order_items || []).map((it, idx) => (
                            <div key={idx} className="flex justify-between text-sm">
                              <span className="text-slate-700">
                                {it.quantity}× {it.products?.name_bn || 'Unknown'}
                              </span>
                              <span className="text-slate-600 font-medium">৳{(it.quantity * it.unit_price).toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {o.payment_method && o.payment_method !== 'cash' && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <div className="text-sm font-semibold text-slate-700 mb-2">Payment: {o.payment_method}</div>
                          {o.payment_transaction_id && (
                            <div className="text-xs text-slate-600 mb-1">TXN: {o.payment_transaction_id}</div>
                          )}
                          {o.payment_sender_number && (
                            <div className="text-xs text-slate-600 mb-2">Sender: {o.payment_sender_number}</div>
                          )}
                          <button
                            onClick={() => {
                              setSelectedPaymentOrder(o);
                              setShowPaymentModal(true);
                            }}
                            className="w-full px-3 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg transition-all transform hover:scale-105 active:scale-95 text-xs shadow-md mt-2"
                          >
                            View Payment Details
                          </button>
                        </div>
                      )}

                      {/* Price Breakdown */}
                      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2 mb-3">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">Product Subtotal:</span>
                          <span className="font-medium text-slate-700">৳{productSubtotal.toFixed(2)}</span>
                        </div>
                        {deliveryCharge > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-600">Delivery Charge:</span>
                            <span className="font-medium text-slate-700">৳{deliveryCharge.toFixed(2)}</span>
                          </div>
                        )}
                        {transactionFee > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-600">
                              Transaction Fee ({o.payment_method === 'bkash' ? 'bKash' : o.payment_method === 'nagad' ? 'Nagad' : ''}):
                            </span>
                            <span className="font-medium text-slate-700">৳{transactionFee.toFixed(2)}</span>
                          </div>
                        )}
                        <div className="flex justify-between items-center pt-2 border-t border-slate-300">
                          <span className="font-semibold text-slate-800">Total Amount:</span>
                          <span className="font-bold text-lg text-orange-600">৳{orderTotal.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 pt-4 border-t border-slate-200">
                      {/* View Details Button - Always visible */}
                      <button
                        onClick={() => {
                          setSelectedOrderForDetails(o);
                          setShowOrderDetailsModal(true);
                        }}
                        className="w-full px-4 py-2 bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white font-semibold rounded-lg transition-all transform hover:scale-105 active:scale-95 text-sm shadow-md mb-2"
                      >
                        📋 View Details
                      </button>

                      {o.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleOrderStatus(o.id, 'confirmed')}
                            className="flex-1 px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold rounded-lg transition-all transform hover:scale-105 active:scale-95 text-sm shadow-md"
                          >
                            Confirm
                          </button>
                          <button
                            onClick={() => handleOrderStatus(o.id, 'cancelled')}
                            className="flex-1 px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold rounded-lg transition-all transform hover:scale-105 active:scale-95 text-sm shadow-md"
                          >
                            Cancel
                          </button>
                        </>
                      )}
                      {o.status === 'confirmed' && (
                        <>
                          <button
                            onClick={() => handleOrderStatus(o.id, 'delivered')}
                            className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg transition-all transform hover:scale-105 active:scale-95 text-sm shadow-md"
                          >
                            Deliver
                          </button>
                          <button
                            onClick={() => handleOrderStatus(o.id, 'returned')}
                            className="px-4 py-2 bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white font-semibold rounded-lg transition-all transform hover:scale-105 active:scale-95 text-sm shadow-md"
                          >
                            Return
                          </button>
                          <button
                            onClick={() => handleOrderStatus(o.id, 'cancelled')}
                            className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold rounded-lg transition-all transform hover:scale-105 active:scale-95 text-sm shadow-md"
                          >
                            Cancel
                          </button>
                        </>
                      )}
                      {o.status === 'delivered' && (
                        <>
                          {/* Return button - only show if within return period and NOT completed/satisfied */}
                          {isWithinReturnPeriod(o) && o.status !== 'completed' && !o.completed_at && (
                            <button
                              onClick={() => handleOrderStatus(o.id, 'returned')}
                              className="flex-1 px-4 py-2 bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white font-semibold rounded-lg transition-all transform hover:scale-105 active:scale-95 text-sm shadow-md"
                            >
                              Return
                            </button>
                          )}
                          {/* Mark as Satisfied button - only show if NOT already completed */}
                          {o.status !== 'completed' && !o.completed_at && (
                            <button
                              onClick={() => handleMarkAsCompleted(o.id)}
                              className={`px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold rounded-lg transition-all transform hover:scale-105 active:scale-95 text-sm shadow-md ${isWithinReturnPeriod(o) && o.status !== 'completed' && !o.completed_at ? 'flex-1' : 'w-full'}`}
                            >
                              Mark as Satisfied
                            </button>
                          )}
                          {o.delivered_at && (
                            <div className="w-full text-xs text-slate-500 mt-2">
                              Delivered: {new Date(o.delivered_at).toLocaleDateString()}
                              {isWithinReturnPeriod(o) && o.status !== 'completed' && !o.completed_at && (
                                <span className="ml-2 text-orange-600 font-medium">
                                  (Returnable until {(() => {
                                    const delivered = new Date(o.delivered_at);
                                    delivered.setDate(delivered.getDate() + 7);
                                    return delivered.toLocaleDateString();
                                  })()})
                                </span>
                              )}
                              {(!isWithinReturnPeriod(o) || o.status === 'completed' || o.completed_at) && o.status !== 'completed' && !o.completed_at && (
                                <span className="ml-2 text-slate-400">Return period expired</span>
                              )}
                              {(o.status === 'completed' || o.completed_at) && (
                                <span className="ml-2 text-green-600 font-medium">✓ Order satisfied - No return available</span>
                              )}
                            </div>
                          )}
                        </>
                      )}
                      {o.status === 'completed' && (
                        <div className="w-full text-center text-sm text-slate-600 py-2">
                          <div className="flex items-center justify-center gap-2">
                            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="font-semibold text-green-700">Order Completed - Customer Satisfied</span>
                          </div>
                          {o.completed_at && (
                            <div className="text-xs text-slate-500 mt-1">
                              Completed on: {new Date(o.completed_at).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      )}
                      {(o.status === 'returned' || o.status === 'cancelled') && (
                        <div className="w-full text-center text-sm text-slate-500 italic py-2">
                          Order {o.status}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {displayedOrders.length === 0 && (
                <div className="col-span-full text-center py-12 text-slate-500">
                  <p className="text-lg">No orders found{orderFilter !== 'all' ? ` with status: ${orderFilter}` : ''}.</p>
                </div>
              )}
            </div>

            {/* Load More Button */}
            {hasMoreOrders && (
              <div className="flex justify-center pt-4">
                <button
                  onClick={() => setOrdersDisplayed(prev => prev + 20)}
                  className="px-6 py-3 bg-gradient-to-r from-slate-700 to-slate-900 hover:from-slate-800 hover:to-slate-900 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:scale-105 active:scale-95"
                  style={{ boxShadow: '0 4px 15px rgba(30, 41, 59, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)' }}
                >
                  Load More ({filteredOrders.length - ordersDisplayed} remaining)
                </button>
              </div>
            )}
          </div>
        )}

        {/* Customers Tab */}
        {activeTab === 'reports' && (
          <div className="bg-gray-50 min-h-screen">
            <ReportsPanel orders={orders} products={products} customers={users} />
          </div>
        )}

        {activeTab === 'customers' && (
          <div className="space-y-6">
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-slate-800">Customers ({filteredCustomers.length})</h2>

              {/* Search Bar */}
              <div className="bg-gradient-to-br from-white to-slate-50 rounded-xl shadow-md border border-slate-200 p-4"
                style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.6)' }}>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by Name, Phone, Address, or District..."
                    value={customerSearchQuery}
                    onChange={(e) => {
                      setCustomerSearchQuery(e.target.value);
                      setCustomersDisplayed(20); // Reset pagination
                    }}
                    className="w-full px-4 py-3 pl-10 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-all shadow-inner"
                  />
                  <svg className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Customers Grid - Mobile First */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {displayedCustomers.map((u) => {
                const customerOrders = orders.filter(o => o.customers?.id === u.id);
                const totalAmount = customerOrders.reduce((sum, order) => {
                  return sum + (order.order_items || []).reduce((itemSum, item) => {
                    return itemSum + (item.quantity * item.unit_price);
                  }, 0);
                }, 0);

                return (
                  <div
                    key={u.id}
                    className="bg-gradient-to-br from-white to-slate-50 rounded-xl shadow-lg hover:shadow-xl transition-all border border-slate-200 p-4 sm:p-6"
                    style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.6)' }}
                  >
                    <div className="space-y-3">
                      <div>
                        <div className="text-lg font-bold text-slate-800">{u.name || '—'}</div>
                        <div className="text-sm text-slate-600 mt-1">📞 {u.phone}</div>
                      </div>

                      {(u.address || u.district) && (
                        <div className="pt-2 border-t border-slate-200">
                          {u.address && (
                            <div className="text-sm text-slate-700 mb-1">
                              <span className="font-semibold">Address:</span> {u.address}
                            </div>
                          )}
                          {u.district && (
                            <div className="text-sm text-slate-700">
                              <span className="font-semibold">District:</span> {u.district}
                            </div>
                          )}
                        </div>
                      )}

                      <div className="pt-2 border-t border-slate-200 space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-semibold text-slate-700">Orders:</span>
                          <span className="text-lg font-bold text-blue-600">{customerOrders.length}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-semibold text-slate-700">Total Value:</span>
                          <span className="text-lg font-bold text-green-600">৳{totalAmount}</span>
                        </div>
                        <div className="text-xs text-slate-500">
                          Registered: {new Date(u.created_at).toLocaleDateString()}
                        </div>
                      </div>

                      <button
                        onClick={() => {
                          setSelectedCustomerOrders(customerOrders);
                          setSelectedCustomer(u);
                          setShowOrderModal(true);
                        }}
                        className="w-full mt-4 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg transition-all transform hover:scale-105 active:scale-95 shadow-md"
                      >
                        View Orders ({customerOrders.length})
                      </button>
                    </div>
                  </div>
                );
              })}
              {displayedCustomers.length === 0 && (
                <div className="col-span-full text-center py-12 text-slate-500">
                  <p className="text-lg">No customers found.</p>
                </div>
              )}
            </div>

            {/* Load More Button */}
            {hasMoreCustomers && (
              <div className="flex justify-center pt-4">
                <button
                  onClick={() => setCustomersDisplayed(prev => prev + 20)}
                  className="px-6 py-3 bg-gradient-to-r from-slate-700 to-slate-900 hover:from-slate-800 hover:to-slate-900 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:scale-105 active:scale-95"
                  style={{ boxShadow: '0 4px 15px rgba(30, 41, 59, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)' }}
                >
                  Load More ({filteredCustomers.length - customersDisplayed} remaining)
                </button>
              </div>
            )}
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            {/* Section Navigation Buttons */}
            <div className="bg-gradient-to-br from-white to-slate-50 rounded-xl shadow-md border border-slate-200 p-4 sticky top-16 md:top-[145px] z-20"
              style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.6)' }}>
              <div className="flex items-center gap-2 mb-3">
                <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
                <span className="text-sm font-semibold text-slate-700">Sections:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setActiveSettingsSection('all')}
                  className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all transform hover:scale-105 active:scale-95 flex items-center gap-2 ${activeSettingsSection === 'all'
                    ? 'bg-gradient-to-r from-slate-700 to-slate-900 text-white shadow-lg'
                    : 'bg-white text-slate-700 shadow-md hover:shadow-lg border border-slate-200'
                    }`}
                  style={activeSettingsSection === 'all' ? { boxShadow: '0 4px 15px rgba(30, 41, 59, 0.3)' } : {}}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                  All
                </button>
                <button
                  onClick={() => setActiveSettingsSection('company')}
                  className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all transform hover:scale-105 active:scale-95 flex items-center gap-2 ${activeSettingsSection === 'company'
                    ? 'bg-gradient-to-r from-orange-600 to-orange-700 text-white shadow-lg'
                    : 'bg-white text-slate-700 shadow-md hover:shadow-lg border border-slate-200'
                    }`}
                  style={activeSettingsSection === 'company' ? { boxShadow: '0 4px 15px rgba(249, 115, 22, 0.3)' } : {}}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  Company
                </button>
                <button
                  onClick={() => setActiveSettingsSection('payment')}
                  className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all transform hover:scale-105 active:scale-95 flex items-center gap-2 ${activeSettingsSection === 'payment'
                    ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg'
                    : 'bg-white text-slate-700 shadow-md hover:shadow-lg border border-slate-200'
                    }`}
                  style={activeSettingsSection === 'payment' ? { boxShadow: '0 4px 15px rgba(37, 99, 235, 0.3)' } : {}}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  Payment
                </button>
                <button
                  onClick={() => setActiveSettingsSection('categories')}
                  className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all transform hover:scale-105 active:scale-95 flex items-center gap-2 ${activeSettingsSection === 'categories'
                    ? 'bg-gradient-to-r from-purple-600 to-purple-700 text-white shadow-lg'
                    : 'bg-white text-slate-700 shadow-md hover:shadow-lg border border-slate-200'
                    }`}
                  style={activeSettingsSection === 'categories' ? { boxShadow: '0 4px 15px rgba(147, 51, 234, 0.3)' } : {}}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  Categories
                </button>
                <button
                  onClick={() => setActiveSettingsSection('hero')}
                  className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all transform hover:scale-105 active:scale-95 flex items-center gap-2 ${activeSettingsSection === 'hero'
                    ? 'bg-gradient-to-r from-indigo-600 to-indigo-700 text-white shadow-lg'
                    : 'bg-white text-slate-700 shadow-md hover:shadow-lg border border-slate-200'
                    }`}
                  style={activeSettingsSection === 'hero' ? { boxShadow: '0 4px 15px rgba(79, 70, 229, 0.3)' } : {}}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Hero Slides
                </button>
              </div>
            </div>

            {/* Company Information Card */}
            {(activeSettingsSection === 'all' || activeSettingsSection === 'company') && (
              <div className="bg-gradient-to-br from-white via-slate-50 to-slate-100 rounded-xl shadow-xl border border-slate-200 p-4 sm:p-6"
                style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.6)' }}>
                <div className="flex items-center mb-6 pb-4 border-b border-slate-300">
                  <svg className="w-6 h-6 text-orange-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <h2 className="text-2xl font-bold bg-gradient-to-r from-slate-700 to-slate-900 bg-clip-text text-transparent">Company Information</h2>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Company Name</label>
                    <input
                      type="text"
                      value={settings.company_name || ''}
                      onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-all shadow-inner"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Company Logo</label>
                    <div className="space-y-3">
                      <input
                        type="text"
                        value={settings.company_logo || ''}
                        onChange={(e) => {
                          setSettings({ ...settings, company_logo: e.target.value });
                          setLogoImageError(false); // Reset error state when URL changes
                        }}
                        className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-all shadow-inner"
                        placeholder="https://example.com/logo.png"
                      />
                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            setLogoFile(e.target.files?.[0] || null);
                            setLogoImageError(false); // Reset error state when new file is selected
                          }}
                          className="flex-1 w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-slate-600 file:text-white hover:file:bg-slate-700 shadow-inner"
                        />
                        {logoFile && (
                          <span className="text-sm text-green-600 font-medium px-3 py-2 bg-green-50 rounded-lg">File: {logoFile.name}</span>
                        )}
                      </div>
                      <div className="mt-2">
                        {settings.company_logo && !logoImageError ? (
                          <div className="space-y-2">
                            <img
                              src={settings.company_logo}
                              alt="Current logo"
                              className="h-16 object-contain border-2 border-slate-300 rounded-xl shadow-inner"
                              onError={() => {
                                setLogoImageError(true);
                              }}
                            />
                            <button
                              type="button"
                              onClick={async () => {
                                if (window.confirm('Remove logo? Company name will be displayed instead.')) {
                                  try {
                                    await updateSetting('company_logo', '');
                                    setSettings({ ...settings, company_logo: '' });
                                    setLogoFile(null);
                                    setLogoImageError(false);
                                    enhancedSetSuccess('Logo removed successfully!');
                                  } catch (err) {
                                    enhancedSetError(err.message || 'Failed to remove logo');
                                  }
                                }
                              }}
                              className="w-full px-4 py-2 bg-red-50 hover:bg-red-100 border border-red-200 text-red-700 font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              Remove Logo
                            </button>
                          </div>
                        ) : (
                          <div className="h-16 flex items-center justify-center border-2 border-dashed border-slate-300 rounded-xl bg-gradient-to-br from-slate-100 to-slate-50">
                            <div className="flex flex-col items-center justify-center gap-2">
                              <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              <span className="text-xs text-slate-500 font-medium">
                                {settings.company_logo ? 'Logo failed to load' : 'No logo uploaded'}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-slate-500">Leave empty to show company name</p>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Company Slogan</label>
                    <input
                      type="text"
                      value={settings.company_slogan || ''}
                      onChange={(e) => setSettings({ ...settings, company_slogan: e.target.value })}
                      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-all shadow-inner"
                      placeholder="e.g. Best Food in Town"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">WhatsApp Number</label>
                    <input
                      type="text"
                      value={settings.whatsapp_number || ''}
                      onChange={(e) => setSettings({ ...settings, whatsapp_number: e.target.value })}
                      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-all shadow-inner"
                      placeholder="+8801734562313"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Company Phone</label>
                    <input
                      type="text"
                      value={settings.company_phone || ''}
                      onChange={(e) => setSettings({ ...settings, company_phone: e.target.value })}
                      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-all shadow-inner"
                      placeholder="+88017..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Company Email</label>
                    <input
                      type="email"
                      value={settings.company_email || ''}
                      onChange={(e) => setSettings({ ...settings, company_email: e.target.value })}
                      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-all shadow-inner"
                      placeholder="example@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Company Address</label>
                    <input
                      type="text"
                      value={settings.company_address || ''}
                      onChange={(e) => setSettings({ ...settings, company_address: e.target.value })}
                      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-all shadow-inner"
                      placeholder="Dhaka, Bangladesh"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Footer Description</label>
                    <textarea
                      value={settings.footer_description || ''}
                      onChange={(e) => setSettings({ ...settings, footer_description: e.target.value })}
                      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-all shadow-inner h-24"
                      placeholder="Short description for the footer..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Social Media Links</label>
                    <div className="space-y-3">
                      <input
                        type="text"
                        value={settings.social_facebook || ''}
                        onChange={(e) => setSettings({ ...settings, social_facebook: e.target.value })}
                        className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-all shadow-inner"
                        placeholder="Facebook URL (e.g. https://facebook.com/...)"
                      />
                      <input
                        type="text"
                        value={settings.social_instagram || ''}
                        onChange={(e) => setSettings({ ...settings, social_instagram: e.target.value })}
                        className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-all shadow-inner"
                        placeholder="Instagram URL"
                      />
                      <input
                        type="text"
                        value={settings.social_youtube || ''}
                        onChange={(e) => setSettings({ ...settings, social_youtube: e.target.value })}
                        className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-all shadow-inner"
                        placeholder="YouTube URL"
                      />
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        setIsUploadingLogo(true);
                        setUploadProgress(0);

                        let logoUrl = settings.company_logo;
                        if (logoFile) {
                          setUploadProgress(30);
                          showToast('Uploading logo... Please wait', 'info', 5000);
                          logoUrl = await uploadImage(logoFile, `logo-${Date.now()}.${logoFile.name.split('.').pop()}`);
                          setUploadProgress(70);
                        }

                        setUploadProgress(80);
                        await updateSetting('company_name', settings.company_name);
                        setUploadProgress(85);
                        await updateSetting('company_logo', logoUrl);
                        setUploadProgress(90);
                        await updateSetting('company_slogan', settings.company_slogan);
                        setUploadProgress(91);
                        await updateSetting('whatsapp_number', settings.whatsapp_number);
                        setUploadProgress(92);
                        await updateSetting('company_phone', settings.company_phone);
                        setUploadProgress(94);
                        await updateSetting('company_email', settings.company_email);
                        setUploadProgress(96);
                        await updateSetting('company_address', settings.company_address);
                        setUploadProgress(96);
                        await updateSetting('footer_description', settings.footer_description);
                        setUploadProgress(97);
                        await updateSetting('social_facebook', settings.social_facebook);
                        await updateSetting('social_instagram', settings.social_instagram);
                        await updateSetting('social_youtube', settings.social_youtube);
                        setUploadProgress(100);

                        setSettings({ ...settings, company_logo: logoUrl });
                        setLogoFile(null);
                        setLogoImageError(false);
                        enhancedSetSuccess('Company settings updated successfully!');

                        // Reset after a short delay
                        setTimeout(() => {
                          setIsUploadingLogo(false);
                          setUploadProgress(0);
                        }, 500);
                      } catch (err) {
                        setIsUploadingLogo(false);
                        setUploadProgress(0);
                        enhancedSetError(err.message || 'Failed to update company settings');
                      }
                    }}
                    disabled={isUploadingLogo}
                    className={`w-full px-6 py-3 bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white font-bold rounded-xl transition-all transform hover:scale-105 active:scale-95 shadow-lg relative overflow-hidden ${isUploadingLogo ? 'opacity-75 cursor-not-allowed' : ''
                      }`}
                    style={{ boxShadow: '0 4px 15px rgba(249, 115, 22, 0.4)' }}
                  >
                    <div className="relative z-10 flex items-center justify-center gap-2">
                      {isUploadingLogo ? (
                        <>
                          <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span>Uploading... {uploadProgress}%</span>
                        </>
                      ) : (
                        <span>Save Company Information</span>
                      )}
                    </div>
                    {isUploadingLogo && (
                      <div
                        className="absolute bottom-0 left-0 h-1 bg-orange-300 transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Payment Gateway Card */}
            {(activeSettingsSection === 'all' || activeSettingsSection === 'payment') && (
              <div className="bg-gradient-to-br from-white via-slate-50 to-slate-100 rounded-xl shadow-xl border border-slate-200 p-4 sm:p-6"
                style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.6)' }}>
                <div className="flex items-center mb-6 pb-4 border-b border-slate-300">
                  <svg className="w-6 h-6 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <h2 className="text-2xl font-bold bg-gradient-to-r from-slate-700 to-slate-900 bg-clip-text text-transparent">Payment Gateway Settings</h2>
                </div>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">bKash Number</label>
                      <input
                        type="text"
                        value={settings.bkash_number || ''}
                        onChange={(e) => setSettings({ ...settings, bkash_number: e.target.value })}
                        className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-all shadow-inner"
                        placeholder="+8801712345678"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Nagad Number</label>
                      <input
                        type="text"
                        value={settings.nagad_number || ''}
                        onChange={(e) => setSettings({ ...settings, nagad_number: e.target.value })}
                        className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-all shadow-inner"
                        placeholder="+8801718765432"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">bKash Fee (%)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={settings.bkash_fee || ''}
                        onChange={(e) => setSettings({ ...settings, bkash_fee: e.target.value })}
                        className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-all shadow-inner"
                        placeholder="1.85"
                      />
                      <p className="text-xs text-slate-500 mt-1">Percentage fee for bKash transactions</p>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Nagad Fee (%)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={settings.nagad_fee || ''}
                        onChange={(e) => setSettings({ ...settings, nagad_fee: e.target.value })}
                        className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-all shadow-inner"
                        placeholder="1.50"
                      />
                      <p className="text-xs text-slate-500 mt-1">Percentage fee for Nagad transactions</p>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Delivery Charge (৳)</label>
                    <input
                      type="number"
                      value={settings.delivery_charge || ''}
                      onChange={(e) => setSettings({ ...settings, delivery_charge: e.target.value })}
                      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-all shadow-inner"
                      placeholder="50"
                    />
                    <p className="text-xs text-slate-500 mt-1">Per order delivery charge</p>
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        await updateSetting('bkash_number', settings.bkash_number);
                        await updateSetting('nagad_number', settings.nagad_number);
                        await updateSetting('bkash_fee', settings.bkash_fee);
                        await updateSetting('nagad_fee', settings.nagad_fee);
                        await updateSetting('delivery_charge', settings.delivery_charge);

                        enhancedSetSuccess('Payment settings updated successfully!');
                      } catch (err) {
                        enhancedSetError(err.message || 'Failed to update payment settings');
                      }
                    }}
                    className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold rounded-xl transition-all transform hover:scale-105 active:scale-95 shadow-lg"
                    style={{ boxShadow: '0 4px 15px rgba(37, 99, 235, 0.4)' }}
                  >
                    Save Payment Settings
                  </button>
                </div>
              </div>
            )}

            {/* Product Categories Management */}
            {(activeSettingsSection === 'all' || activeSettingsSection === 'categories') && (
              <div className="bg-gradient-to-br from-white via-slate-50 to-slate-100 rounded-xl shadow-xl border border-slate-200 p-4 sm:p-6"
                style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.6)' }}>
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-300">
                  <h2 className="text-2xl font-bold bg-gradient-to-r from-slate-700 to-slate-900 bg-clip-text text-transparent">Product Categories</h2>
                </div>
                <div className="space-y-4">
                  {/* Add Category Form */}
                  <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-inner">
                    {editingCategory ? (
                      <div className="flex flex-col sm:flex-row gap-3">
                        <input
                          type="text"
                          value={newCategoryName}
                          onChange={(e) => setNewCategoryName(e.target.value)}
                          className="flex-1 px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-all shadow-inner"
                          placeholder="Edit category name"
                        />
                        <button
                          onClick={handleUpdateCategory}
                          className="px-4 py-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold rounded-xl transition-all transform hover:scale-105 active:scale-95 shadow-md"
                        >
                          Update
                        </button>
                        <button
                          onClick={() => {
                            setEditingCategory(null);
                            setNewCategoryName('');
                          }}
                          className="px-4 py-3 bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white font-semibold rounded-xl transition-all transform hover:scale-105 active:scale-95 shadow-md"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-col sm:flex-row gap-3">
                        <input
                          type="text"
                          value={newCategoryName}
                          onChange={(e) => setNewCategoryName(e.target.value)}
                          className="flex-1 px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-all shadow-inner"
                          placeholder="Enter new category name"
                        />
                        <button
                          onClick={handleAddCategory}
                          className="px-4 py-3 bg-gradient-to-r from-slate-700 to-slate-900 hover:from-slate-800 hover:to-slate-900 text-white font-semibold rounded-xl transition-all transform hover:scale-105 active:scale-95 shadow-md"
                          style={{ boxShadow: '0 4px 15px rgba(30, 41, 59, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)' }}
                        >
                          Add Category
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Categories List */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {categories.map((category) => (
                      <div key={category} className="bg-white border border-slate-200 rounded-xl p-4 shadow-md flex items-center justify-between">
                        <span className="font-semibold text-slate-800 flex-1">{category}</span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEditCategory(category)}
                            className="px-3 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg transition-all transform hover:scale-105 active:scale-95 text-sm shadow-md"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteCategory(category)}
                            className="px-3 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold rounded-lg transition-all transform hover:scale-105 active:scale-95 text-sm shadow-md"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  {categories.length === 0 && (
                    <div className="text-center py-8 text-slate-500">
                      <p>No categories found. Add a new category to get started.</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Hero Slides Card */}
            {(activeSettingsSection === 'all' || activeSettingsSection === 'hero') && (
              <div className="bg-gradient-to-br from-white via-slate-50 to-slate-100 rounded-xl shadow-xl border border-slate-200 p-4 sm:p-6"
                style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.6)' }}>
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-300">
                  <h2 className="text-2xl font-bold bg-gradient-to-r from-slate-700 to-slate-900 bg-clip-text text-transparent">Hero Slides</h2>
                  <button
                    onClick={handleAddHeroSlide}
                    className="px-4 py-2 bg-gradient-to-r from-slate-700 to-slate-900 hover:from-slate-800 hover:to-slate-900 text-white font-semibold rounded-xl transition-all transform hover:scale-105 active:scale-95 shadow-md"
                    style={{ boxShadow: '0 4px 15px rgba(30, 41, 59, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)' }}
                  >
                    + Add Slide
                  </button>
                </div>

                {/* Hero Slides List */}
                <div className="space-y-3">
                  {heroSlides.map((slide) => (
                    <div key={slide.id} className="bg-white border border-slate-200 rounded-xl p-4 shadow-md flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                      <div className="flex items-center gap-4 flex-1">
                        {slide.image_url && (
                          <img src={slide.image_url} alt={slide.title} className="w-20 h-12 object-cover rounded-lg border border-slate-200" />
                        )}
                        <div>
                          <div className="font-bold text-slate-800">{slide.title}</div>
                          <div className="text-sm text-slate-600">{slide.subtitle}</div>
                          <div className="text-xs text-slate-500">Order: {slide.order_index}</div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditHeroSlide(slide)}
                          className="px-3 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg transition-all transform hover:scale-105 active:scale-95 text-sm shadow-md"
                        >
                          Edit
                        </button>
                        <button
                          onClick={async () => {
                            if (window.confirm('Delete this slide?')) {
                              try {
                                await deleteHeroSlide(slide.id);
                                enhancedSetSuccess('Slide deleted!');
                                await loadSettings();
                              } catch (err) {
                                enhancedSetError(err.message || 'Failed to delete hero slide');
                              }
                            }
                          }}
                          className="px-3 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold rounded-lg transition-all transform hover:scale-105 active:scale-95 text-sm shadow-md"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                  {heroSlides.length === 0 && (
                    <div className="text-center py-8 text-slate-500">
                      <p>No hero slides found. Click "Add Slide" to create your first slide.</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Mobile Footer Menu - Sticky */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800 shadow-2xl border-t border-slate-600 z-50"
          style={{ boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)' }}>
          <div className="flex items-center justify-around px-2 py-2">
            {[
              {
                id: 'products',
                label: 'Products',
                count: products.length,
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                )
              },
              {
                id: 'orders',
                label: 'Orders',
                count: orders.length,
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                )
              },
              {
                id: 'customers',
                label: 'Customers',
                count: users.length,
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.196-2.134M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.196-2.134M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                )
              },
              {
                id: 'reports',
                label: 'Reports',
                count: null,
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                )
              },
              {
                id: 'settings',
                label: 'Settings',
                count: null,
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                )
              },
              {
                id: 'profile',
                label: 'Profile',
                count: null,
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                )
              }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  // Scroll to top on mobile when switching tabs
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className={`flex flex-col items-center justify-center gap-1 px-2 py-2 min-w-[60px] rounded-lg transition-all ${activeTab === tab.id
                  ? 'bg-gradient-to-br from-slate-600 to-slate-700 text-white shadow-lg transform scale-105'
                  : 'text-slate-300 hover:text-white hover:bg-slate-700/50'
                  }`}
                style={activeTab === tab.id ? {
                  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                } : {}}
              >
                <div className="relative">
                  {tab.icon}
                  {tab.count !== null && (
                    <span className="absolute -top-2 -right-2 bg-orange-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center border-2 border-slate-700">
                      {tab.count > 99 ? '99+' : tab.count}
                    </span>
                  )}
                </div>
                <span className={`text-xs font-semibold ${activeTab === tab.id ? 'text-white' : 'text-slate-400'}`}>
                  {tab.label}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Bottom padding for mobile footer */}
        <div className="md:hidden pb-20"></div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="max-w-2xl mx-auto space-y-6">
            <div className="bg-gradient-to-br from-white via-slate-50 to-slate-100 rounded-xl shadow-xl border border-slate-200 p-4 sm:p-6"
              style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.6)' }}>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-slate-700 to-slate-900 bg-clip-text text-transparent mb-6 pb-4 border-b border-slate-300">Profile Settings</h2>
              {profile && (
                <div className="space-y-6">
                  {/* Account Info (Read-only) */}
                  <div className="border-b border-slate-200 pb-6">
                    <h3 className="text-lg font-bold text-slate-800 mb-4">Account Information</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Email</label>
                        <div className="px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 font-medium">{profile.email}</div>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Role</label>
                        <div className="px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 font-medium">{profile.role || 'user'}</div>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Created</label>
                        <div className="px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 font-medium">
                          {new Date(profile.created_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Change Email */}
                  <div className="border-b border-slate-200 pb-6">
                    <h3 className="text-lg font-bold text-slate-800 mb-4">Change Email</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">New Email</label>
                        <input
                          type="email"
                          value={profileEmail}
                          onChange={(e) => setProfileEmail(e.target.value)}
                          className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-all shadow-inner"
                          placeholder="Enter new email"
                        />
                      </div>
                      <button
                        onClick={async () => {
                          if (!profileEmail || profileEmail === profile.email) {
                            enhancedSetError('Please enter a different email address');
                            return;
                          }
                          try {
                            await updateUserEmail(profileEmail);
                            const updatedProfile = await getMyProfile();
                            setProfile(updatedProfile);
                            enhancedSetSuccess('Email updated successfully! A confirmation email has been sent.');
                          } catch (err) {
                            enhancedSetError(err.message || 'Failed to update email');
                          }
                        }}
                        className="w-full px-6 py-3 bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white font-bold rounded-xl transition-all transform hover:scale-105 active:scale-95 shadow-lg"
                        style={{ boxShadow: '0 4px 15px rgba(249, 115, 22, 0.4)' }}
                      >
                        Update Email
                      </button>
                    </div>
                  </div>

                  {/* Change Password */}
                  <div>
                    <h3 className="text-lg font-bold text-slate-800 mb-4">Change Password</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">New Password</label>
                        <input
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-all shadow-inner"
                          placeholder="Enter new password"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Confirm Password</label>
                        <input
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-all shadow-inner"
                          placeholder="Confirm new password"
                        />
                      </div>
                      <button
                        onClick={async () => {
                          if (!newPassword || newPassword.length < 6) {
                            enhancedSetError('Password must be at least 6 characters');
                            return;
                          }
                          if (newPassword !== confirmPassword) {
                            enhancedSetError('Passwords do not match');
                            return;
                          }
                          try {
                            await updateUserPassword(newPassword);
                            setNewPassword('');
                            setConfirmPassword('');
                            enhancedSetSuccess('Password updated successfully!');
                          } catch (err) {
                            enhancedSetError(err.message || 'Failed to update password');
                          }
                        }}
                        className="w-full px-6 py-3 bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white font-bold rounded-xl transition-all transform hover:scale-105 active:scale-95 shadow-lg"
                        style={{ boxShadow: '0 4px 15px rgba(249, 115, 22, 0.4)' }}
                      >
                        Update Password
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Hero Slide Modal */}
      <HeroSlideModal
        slide={editingHeroSlide}
        isOpen={showHeroSlideModal}
        onClose={() => {
          setShowHeroSlideModal(false);
          setEditingHeroSlide(null);
        }}
        onSave={handleHeroSlideSave}
        isUploading={isUploadingHeroImage}
      />

      {/* Order Modal */}
      {showOrderModal && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-2 sm:p-4"
          onClick={() => {
            setShowOrderModal(false);
            setSelectedCustomerOrders([]);
            setSelectedCustomer(null);
            setError('');
            setSuccess('');
          }}
        >
          <div
            className="bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100 rounded-2xl shadow-2xl border border-slate-200 max-w-4xl w-full max-h-[95vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.6)'
            }}
          >
            <div className="p-4 sm:p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-300">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-slate-700 to-slate-900 bg-clip-text text-transparent">
                    Customer Orders
                  </h2>
                  {selectedCustomer && (
                    <p className="text-sm text-slate-600 mt-1">
                      {selectedCustomer.name} ({selectedCustomer.phone})
                    </p>
                  )}
                </div>
                <button
                  onClick={() => {
                    setShowOrderModal(false);
                    setSelectedCustomerOrders([]);
                    setSelectedCustomer(null);
                    setError('');
                    setSuccess('');
                  }}
                  className="text-slate-400 hover:text-slate-600 transition-colors p-2 hover:bg-slate-200 rounded-lg"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Messages */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                  {error}
                </div>
              )}
              {success && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
                  {success}
                </div>
              )}

              {/* Orders List */}
              {selectedCustomerOrders.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <p>No orders found for this customer.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {selectedCustomerOrders.map((order) => {
                    const orderTotal = (order.order_items || []).reduce((sum, item) => {
                      return sum + (item.quantity * item.unit_price);
                    }, 0);

                    return (
                      <div key={order.id} className="bg-gradient-to-br from-white to-slate-50 border border-slate-200 rounded-xl p-4 sm:p-6 shadow-md mb-4"
                        style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.6)' }}>
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <p className="font-semibold text-gray-900">
                              Order #{order.id.substring(0, 8)}
                            </p>
                            <p className="text-sm text-gray-600">
                              {new Date(order.created_at).toLocaleString()}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <span className={`px-3 py-1 rounded text-xs font-medium ${order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              order.status === 'confirmed' ? 'bg-blue-100 text-blue-800' :
                                order.status === 'delivered' ? 'bg-green-100 text-green-800' :
                                  order.status === 'returned' ? 'bg-red-100 text-red-800' :
                                    order.status === 'payment_failed' ? 'bg-purple-100 text-purple-800' :
                                      'bg-gray-100 text-gray-800'
                              }`}>
                              {order.status}
                            </span>
                            <span className={`px-3 py-1 rounded text-xs font-medium ${order.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                              'bg-red-100 text-red-800'
                              }`}>
                              {order.payment_status}
                            </span>
                          </div>
                        </div>

                        {/* Order Items */}
                        <div className="border-t pt-3 space-y-2">
                          {order.order_items && order.order_items.map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between text-sm">
                              <div className="flex items-center gap-3">
                                <span className="text-gray-600">
                                  {item.quantity}× {item.products?.name_bn || 'Unknown Product'}
                                </span>
                              </div>
                              <span className="font-medium text-gray-900">
                                ৳{item.quantity * item.unit_price}
                              </span>
                            </div>
                          ))}
                        </div>

                        {/* Payment Information */}
                        {order.payment_method && order.payment_method !== 'cash' && (
                          <div className="border-t pt-3 mt-3 bg-blue-50 rounded-lg p-3">
                            <h4 className="font-semibold text-gray-900 mb-2">Payment Details ({order.payment_method})</h4>
                            <div className="space-y-1 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Transaction ID:</span>
                                <span className="font-medium text-gray-900">{order.payment_transaction_id || 'N/A'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Sender Number:</span>
                                <span className="font-medium text-gray-900">{order.payment_sender_number || 'N/A'}</span>
                              </div>
                            </div>
                            {order.payment_status === 'unpaid' && order.status === 'pending' && (
                              <div className="mt-3 flex gap-2">
                                <button
                                  onClick={() => handlePaymentVerification(order.id, true)}
                                  className="flex-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded text-sm font-semibold"
                                >
                                  ✓ Accept Payment
                                </button>
                                <button
                                  onClick={() => handlePaymentVerification(order.id, false)}
                                  className="flex-1 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded text-sm font-semibold"
                                >
                                  ✗ Reject Payment
                                </button>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Order Total */}
                        <div className="border-t pt-3 mt-3 flex justify-between items-center">
                          <span className="font-semibold text-gray-900">Total:</span>
                          <span className="font-bold text-lg text-orange-600">৳{orderTotal}</span>
                        </div>

                        {/* Action Buttons */}
                        <div className="border-t border-slate-200 pt-3 mt-3 flex gap-2 flex-wrap">
                          {order.status === 'pending' && (
                            <>
                              <button
                                onClick={() => handleOrderStatus(order.id, 'confirmed')}
                                className="flex-1 px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold rounded-lg transition-all transform hover:scale-105 active:scale-95 text-sm shadow-md"
                              >
                                Confirm
                              </button>
                              <button
                                onClick={() => handleOrderStatus(order.id, 'cancelled')}
                                className="flex-1 px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold rounded-lg transition-all transform hover:scale-105 active:scale-95 text-sm shadow-md"
                              >
                                Cancel
                              </button>
                            </>
                          )}
                          {order.status === 'confirmed' && (
                            <>
                              <button
                                onClick={() => handleOrderStatus(order.id, 'delivered')}
                                className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg transition-all transform hover:scale-105 active:scale-95 text-sm shadow-md"
                              >
                                Delivered
                              </button>
                              <button
                                onClick={() => handleOrderStatus(order.id, 'returned')}
                                className="px-4 py-2 bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white font-semibold rounded-lg transition-all transform hover:scale-105 active:scale-95 text-sm shadow-md"
                              >
                                Return
                              </button>
                              <button
                                onClick={() => handleOrderStatus(order.id, 'cancelled')}
                                className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold rounded-lg transition-all transform hover:scale-105 active:scale-95 text-sm shadow-md"
                              >
                                Cancel
                              </button>
                            </>
                          )}
                          {order.status === 'delivered' && (
                            <>
                              <button
                                onClick={() => handleOrderStatus(order.id, 'returned')}
                                className="flex-1 px-4 py-2 bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white font-semibold rounded-lg transition-all transform hover:scale-105 active:scale-95 text-sm shadow-md"
                              >
                                Return
                              </button>
                              <button
                                onClick={() => handleOrderStatus(order.id, 'cancelled')}
                                className="flex-1 px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold rounded-lg transition-all transform hover:scale-105 active:scale-95 text-sm shadow-md"
                              >
                                Cancel
                              </button>
                            </>
                          )}
                          {order.status === 'returned' && (
                            <div className="w-full text-center text-sm text-slate-500 italic py-2">
                              Order has been returned
                            </div>
                          )}
                          {order.status === 'cancelled' && (
                            <div className="w-full text-center text-sm text-slate-500 italic py-2">
                              Order has been cancelled
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Payment Details Modal */}
      {showPaymentModal && selectedPaymentOrder && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4"
          onClick={() => {
            setShowPaymentModal(false);
            setSelectedPaymentOrder(null);
          }}
        >
          <div
            className="bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100 rounded-2xl shadow-2xl border border-slate-200 max-w-md w-full max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.6)'
            }}
          >
            <div className="p-4 sm:p-6 overflow-y-auto">
              {/* Header */}
              <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-300">
                <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-slate-700 to-slate-900 bg-clip-text text-transparent">Payment Verification</h2>
                <button
                  onClick={() => {
                    setShowPaymentModal(false);
                    setSelectedPaymentOrder(null);
                  }}
                  className="text-slate-400 hover:text-slate-600 transition-colors p-2 hover:bg-slate-200 rounded-lg"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Order Info */}
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-1">Order #</p>
                <p className="font-semibold text-gray-900">{selectedPaymentOrder.id.substring(0, 8)}</p>
                <p className="text-xs text-gray-500 mt-1">Status: {selectedPaymentOrder.status} | Payment: {selectedPaymentOrder.payment_status}</p>
              </div>

              {/* Payment Details */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                <h3 className="font-semibold text-gray-900 mb-2">Payment Details</h3>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Payment Method:</span>
                    <span className="font-medium text-gray-900 capitalize">{selectedPaymentOrder.payment_method}</span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-gray-600">Transaction ID:</span>
                    <span className="font-medium text-gray-900 font-mono">{selectedPaymentOrder.payment_transaction_id || 'N/A'}</span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-gray-600">Sender Number:</span>
                    <span className="font-medium text-gray-900">{selectedPaymentOrder.payment_sender_number || 'N/A'}</span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-gray-600">Payment Status:</span>
                    <span className={`font-medium ${selectedPaymentOrder.payment_status === 'paid' ? 'text-green-600' : 'text-red-600'
                      }`}>
                      {selectedPaymentOrder.payment_status}
                    </span>
                  </div>

                  {/* Price Breakdown */}
                  <div className="border-t pt-3 mt-3 space-y-2">
                    {/* Product Subtotal */}
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Product Subtotal:</span>
                      <span className="font-medium text-gray-900">
                        ৳{((selectedPaymentOrder.order_items || []).reduce((sum, item) =>
                          sum + (item.quantity * item.unit_price), 0
                        )).toFixed(2)}
                      </span>
                    </div>
                    {/* Delivery Charge */}
                    {(() => {
                      const deliveryChargeRaw = selectedPaymentOrder.delivery_charge || 0;
                      // Only divide if value >= 1000 (new format stored as * 100)
                      const deliveryCharge = deliveryChargeRaw >= 1000 ? (deliveryChargeRaw / 100) : deliveryChargeRaw;
                      return deliveryCharge > 0 ? (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Delivery Charge:</span>
                          <span className="font-medium text-gray-900">
                            ৳{deliveryCharge.toFixed(2)}
                          </span>
                        </div>
                      ) : null;
                    })()}
                    {/* Transaction Fee */}
                    {(() => {
                      const transactionFeeRaw = selectedPaymentOrder.transaction_fee || 0;
                      // Only divide if value >= 100 (new format stored as * 100)
                      const transactionFee = transactionFeeRaw >= 100 ? (transactionFeeRaw / 100) : transactionFeeRaw;
                      return transactionFee > 0 ? (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">
                            Transaction Fee ({selectedPaymentOrder.payment_method === 'bkash' ? 'bKash' : selectedPaymentOrder.payment_method === 'nagad' ? 'Nagad' : ''}):
                          </span>
                          <span className="font-medium text-gray-900">
                            ৳{transactionFee.toFixed(2)}
                          </span>
                        </div>
                      ) : null;
                    })()}
                    {/* Total Amount */}
                    <div className="flex justify-between font-bold pt-2 border-t border-gray-300">
                      <span className="text-gray-900">Total Amount:</span>
                      <span className="text-orange-600 text-lg">
                        ৳{(() => {
                          const productSubtotal = (selectedPaymentOrder.order_items || []).reduce((sum, item) =>
                            sum + (item.quantity * item.unit_price), 0
                          );
                          const deliveryChargeRaw = selectedPaymentOrder.delivery_charge || 0;
                          const transactionFeeRaw = selectedPaymentOrder.transaction_fee || 0;
                          const totalAmountRaw = selectedPaymentOrder.total_amount || 0;

                          // Apply conversion only if values are in new format (>= thresholds)
                          const deliveryCharge = deliveryChargeRaw >= 1000 ? (deliveryChargeRaw / 100) : deliveryChargeRaw;
                          const transactionFee = transactionFeeRaw >= 100 ? (transactionFeeRaw / 100) : transactionFeeRaw;

                          const total = totalAmountRaw > 0
                            ? (totalAmountRaw >= 10000 ? (totalAmountRaw / 100) : totalAmountRaw)
                            : (productSubtotal + deliveryCharge + transactionFee);

                          return total.toFixed(2);
                        })()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons - Always Show */}
              <div className="mt-6 space-y-3">
                {(selectedPaymentOrder.payment_status === 'unpaid' || !selectedPaymentOrder.payment_status || selectedPaymentOrder.payment_status === 'pending') && (
                  <>
                    <button
                      onClick={() => {
                        handlePaymentVerification(selectedPaymentOrder.id, true);
                        setShowPaymentModal(false);
                        setSelectedPaymentOrder(null);
                      }}
                      className="w-full px-4 py-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-lg font-semibold transition-all transform hover:scale-105 active:scale-95 shadow-lg"
                      style={{ boxShadow: '0 4px 15px rgba(34, 197, 94, 0.4)' }}
                    >
                      ✓ Accept Payment & Confirm Order
                    </button>
                    <button
                      onClick={() => {
                        handlePaymentVerification(selectedPaymentOrder.id, false);
                        setShowPaymentModal(false);
                        setSelectedPaymentOrder(null);
                      }}
                      className="w-full px-4 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-lg font-semibold transition-all transform hover:scale-105 active:scale-95 shadow-lg"
                      style={{ boxShadow: '0 4px 15px rgba(220, 38, 38, 0.4)' }}
                    >
                      ✗ Reject Payment & Cancel Order
                    </button>
                  </>
                )}
                {selectedPaymentOrder.payment_status === 'paid' && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-700 text-center font-semibold">
                      ✓ Payment Already Verified
                    </p>
                    <p className="text-xs text-green-600 text-center mt-1">
                      Order Status: {selectedPaymentOrder.status}
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div className="border-t p-3 bg-white/70 rounded-b-2xl">
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  setSelectedPaymentOrder(null);
                }}
                className="w-full px-4 py-3 border-2 border-slate-300 text-slate-700 hover:bg-slate-100 rounded-lg font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Order Details Modal */}
      {showOrderDetailsModal && selectedOrderForDetails && (() => {
        const o = selectedOrderForDetails;

        // Helper function to calculate order totals
        const calculateOrderTotals = (order) => {
          const productSubtotal = (order.order_items || []).reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
          const deliveryChargeRaw = order.delivery_charge || 0;
          const transactionFeeRaw = order.transaction_fee || 0;
          const totalAmountRaw = order.total_amount || 0;

          const needsDivisionDelivery = deliveryChargeRaw >= 1000;
          const needsDivisionTransaction = transactionFeeRaw >= 100;
          const needsDivisionTotal = totalAmountRaw >= 10000;

          const deliveryCharge = needsDivisionDelivery ? (deliveryChargeRaw / 100) : deliveryChargeRaw;
          const transactionFee = needsDivisionTransaction ? (transactionFeeRaw / 100) : transactionFeeRaw;
          const orderTotal = totalAmountRaw > 0
            ? (needsDivisionTotal ? (totalAmountRaw / 100) : totalAmountRaw)
            : (productSubtotal + deliveryCharge + transactionFee);

          return { productSubtotal, deliveryCharge, transactionFee, orderTotal };
        };

        const totals = calculateOrderTotals(o);

        return (
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowOrderDetailsModal(false);
                setSelectedOrderForDetails(null);
              }
            }}
          >
            <div
              className="bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100 rounded-2xl shadow-2xl border border-slate-200 max-w-2xl w-full my-8"
              onClick={(e) => e.stopPropagation()}
              style={{ maxHeight: '90vh', overflowY: 'auto' }}
            >
              <div className="p-4 sm:p-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-300">
                  <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-slate-700 to-slate-900 bg-clip-text text-transparent">
                    Order Details
                  </h2>
                  <button
                    onClick={() => {
                      setShowOrderDetailsModal(false);
                      setSelectedOrderForDetails(null);
                    }}
                    className="text-slate-400 hover:text-slate-600 transition-colors p-2 hover:bg-slate-200 rounded-lg"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Order Information */}
                <div className="space-y-4">
                  {/* Order ID & Status */}
                  <div className="bg-white rounded-lg p-4 border border-slate-200">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs font-semibold text-slate-500 mb-1">Order ID</div>
                        <div className="text-sm font-mono text-slate-800">{o.id}</div>
                      </div>
                      <div>
                        <div className="text-xs font-semibold text-slate-500 mb-1">Order Date</div>
                        <div className="text-sm text-slate-800">{new Date(o.created_at).toLocaleString()}</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
                      <div>
                        <div className="text-xs font-semibold text-slate-500 mb-1">Status</div>
                        <span className={`inline-block px-3 py-1 rounded-lg text-xs font-bold ${o.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          o.status === 'confirmed' ? 'bg-blue-100 text-blue-800' :
                            o.status === 'delivered' ? 'bg-green-100 text-green-800' :
                              o.status === 'completed' ? 'bg-emerald-100 text-emerald-800' :
                                o.status === 'returned' ? 'bg-red-100 text-red-800' :
                                  o.status === 'payment_failed' ? 'bg-purple-100 text-purple-800' :
                                    'bg-gray-100 text-gray-800'
                          }`}>
                          {o.status}
                        </span>
                      </div>
                      <div>
                        <div className="text-xs font-semibold text-slate-500 mb-1">Payment Status</div>
                        <span className={`inline-block px-3 py-1 rounded-lg text-xs font-bold ${o.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                          'bg-red-100 text-red-800'
                          }`}>
                          {o.payment_status}
                        </span>
                      </div>
                    </div>
                    {o.delivered_at && (
                      <div className="mt-3 pt-3 border-t border-slate-200">
                        <div className="text-xs font-semibold text-slate-500 mb-1">Delivered At</div>
                        <div className="text-sm text-slate-800">{new Date(o.delivered_at).toLocaleString()}</div>
                      </div>
                    )}
                    {o.completed_at && (
                      <div className="mt-3 pt-3 border-t border-slate-200">
                        <div className="text-xs font-semibold text-slate-500 mb-1">Completed At</div>
                        <div className="text-sm text-slate-800">{new Date(o.completed_at).toLocaleString()}</div>
                      </div>
                    )}
                  </div>

                  {/* Customer Information */}
                  <div className="bg-white rounded-lg p-4 border border-slate-200">
                    <h3 className="text-sm font-bold text-slate-700 mb-3">Customer Information</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-600">Name:</span>
                        <span className="text-sm font-medium text-slate-800">{o.customers?.name || '—'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-600">Phone:</span>
                        <span className="text-sm font-medium text-slate-800">{o.customers?.phone || '—'}</span>
                      </div>
                      {o.customers?.address && (
                        <div className="flex justify-between">
                          <span className="text-sm text-slate-600">Address:</span>
                          <span className="text-sm font-medium text-slate-800 text-right">{o.customers.address}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Order Items */}
                  <div className="bg-white rounded-lg p-4 border border-slate-200">
                    <h3 className="text-sm font-bold text-slate-700 mb-3">Order Items</h3>
                    <div className="space-y-3">
                      {(o.order_items || []).map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center pb-2 border-b border-slate-100 last:border-0">
                          <div className="flex-1">
                            <div className="text-sm font-medium text-slate-800">
                              {item.products?.name_bn || item.products?.name_en || 'Unknown Product'}
                            </div>
                            <div className="text-xs text-slate-500">
                              Quantity: {item.quantity} × ৳{item.unit_price.toFixed(2)}
                            </div>
                          </div>
                          <div className="text-sm font-semibold text-slate-800">
                            ৳{(item.quantity * item.unit_price).toFixed(2)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Payment Information */}
                  {o.payment_method && (
                    <div className="bg-white rounded-lg p-4 border border-slate-200">
                      <h3 className="text-sm font-bold text-slate-700 mb-3">Payment Information</h3>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-slate-600">Payment Method:</span>
                          <span className="text-sm font-medium text-slate-800 capitalize">{o.payment_method}</span>
                        </div>
                        {o.payment_transaction_id && (
                          <div className="flex justify-between">
                            <span className="text-sm text-slate-600">Transaction ID:</span>
                            <span className="text-sm font-medium text-slate-800 font-mono">{o.payment_transaction_id}</span>
                          </div>
                        )}
                        {o.payment_sender_number && (
                          <div className="flex justify-between">
                            <span className="text-sm text-slate-600">Sender Number:</span>
                            <span className="text-sm font-medium text-slate-800">{o.payment_sender_number}</span>
                          </div>
                        )}
                        {o.verification_code && (
                          <div className="flex justify-between">
                            <span className="text-sm text-slate-600">Verification Code:</span>
                            <span className="text-sm font-medium text-slate-800 font-mono">{o.verification_code}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Price Breakdown */}
                  <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4 border border-orange-200">
                    <h3 className="text-sm font-bold text-slate-700 mb-3">Price Breakdown</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Product Subtotal:</span>
                        <span className="font-medium text-slate-700">৳{totals.productSubtotal.toFixed(2)}</span>
                      </div>
                      {totals.deliveryCharge > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">Delivery Charge:</span>
                          <span className="font-medium text-slate-700">৳{totals.deliveryCharge.toFixed(2)}</span>
                        </div>
                      )}
                      {totals.transactionFee > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">
                            Transaction Fee ({o.payment_method === 'bkash' ? 'bKash' : o.payment_method === 'nagad' ? 'Nagad' : ''}):
                          </span>
                          <span className="font-medium text-slate-700">৳{totals.transactionFee.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center pt-3 border-t border-orange-300">
                        <span className="font-bold text-slate-800">Total Amount:</span>
                        <span className="font-bold text-xl text-orange-600">৳{totals.orderTotal.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Notes */}
                  {o.notes && (
                    <div className="bg-white rounded-lg p-4 border border-slate-200">
                      <h3 className="text-sm font-bold text-slate-700 mb-2">Notes</h3>
                      <div className="text-sm text-slate-700 whitespace-pre-wrap">{o.notes}</div>
                    </div>
                  )}

                  {/* Close Button */}
                  <button
                    onClick={() => {
                      setShowOrderDetailsModal(false);
                      setSelectedOrderForDetails(null);
                    }}
                    className="w-full px-4 py-3 bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white rounded-lg font-semibold transition-all transform hover:scale-105 active:scale-95 shadow-lg mt-4"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Footer - Copyright */}
      <footer className="mt-12 border-t border-slate-300 pt-6 pb-4 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-600 text-sm mb-2 bengali-text">
            © {settings.company_name || 'ঘরোয়া খাবার'} {new Date().getFullYear()}. সকল অধিকার সংরক্ষিত।
          </p>
          <p className="text-slate-500 text-xs">
            Powered by{' '}
            <a
              href="https://technoflicker.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-orange-600 hover:text-orange-700 transition-colors underline font-medium"
            >
              TechnoFlicker
            </a>
            {' '}| Version: V-RBZ-02-26-V3
          </p>
        </div>
      </footer>
    </div>
  );
};

export default AdminPanelEnhanced;

